import React, { useMemo, useState } from 'react';
import { Lock, Search, X } from 'lucide-react';
import styles from './AccessMatrix.module.css';

/**
 * Access matrix: one switch per button / rule of the blueprint.
 *
 * props
 *   blueprint, categories   from the server
 *   value                   { module: { actions: {key: bool}, constraints: {key: value} } }
 *   onChange(next)
 *   limit                   what may be given (grantable). null = no limit (HQ editing a branch limit)
 *   ceiling                 HQ branch limit, only to explain why something is locked
 *   readOnly
 *   mode                    'USER' | 'CEILING'
 */
const RISK = { high: 'High', medium: 'Medium', low: '' };

const numberOr = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

export default function AccessMatrixEditor({ blueprint = [], categories = [], value = {}, onChange, limit = null, ceiling = null, readOnly = false, mode = 'USER' }) {
    const modules = useMemo(() => blueprint.filter((m) => !m.hqOnly), [blueprint]);
    const [active, setActive] = useState(modules[0]?.id || '');
    const [search, setSearch] = useState('');

    const allowed = (mod, key) => !limit || limit?.[mod]?.actions?.[key] === true;
    const lockReason = (mod, key) => {
        if (ceiling && ceiling?.[mod]?.actions?.[key] === false) return 'Blocked by HQ';
        if (limit && limit?.[mod]?.actions?.[key] !== true) return mode === 'CEILING' ? 'Not allowed' : 'Above your access';
        return '';
    };
    const isOn = (mod, key) => value?.[mod]?.actions?.[key] === true;

    const update = (mod, patch) => {
        if (readOnly) return;
        const cur = value?.[mod] || { actions: {}, constraints: {} };
        onChange({ ...value, [mod]: { actions: { ...cur.actions, ...(patch.actions || {}) }, constraints: { ...cur.constraints, ...(patch.constraints || {}) } } });
    };

    const setAction = (mod, key, on) => {
        if (on && !allowed(mod, key)) return;
        update(mod, { actions: { [key]: on } });
    };

    const setAll = (mod, on) => {
        const m = modules.find((x) => x.id === mod);
        const actions = {};
        Object.keys(m.actions).forEach((k) => { actions[k] = on ? (allowed(mod, k) || isOn(mod, k)) : false; });
        update(mod, { actions });
    };

    /* ---------- constraint limits ---------- */
    const constraintLimit = (mod, key) => (limit ? limit?.[mod]?.constraints?.[key] : undefined);
    const setConstraint = (mod, key, def, raw) => {
        const lim = constraintLimit(mod, key);
        let v = raw;
        if (def.type === 'toggle') { if (raw && lim === false) return; v = Boolean(raw); }
        if (def.type === 'number') {
            v = Math.max(0, numberOr(raw));
            if (lim !== undefined && numberOr(lim) > 0 && (v === 0 || v > numberOr(lim))) v = numberOr(lim);
        }
        if (def.type === 'select' && lim !== undefined && numberOr(raw) > numberOr(lim)) v = String(lim);
        update(mod, { constraints: { [key]: v } });
    };

    /* ---------- counts ---------- */
    const countOn = (m) => Object.keys(m.actions).filter((k) => isOn(m.id, k)).length;
    const total = modules.reduce((s, m) => s + Object.keys(m.actions).length, 0);
    const totalOn = modules.reduce((s, m) => s + countOn(m), 0);

    const q = search.trim().toLowerCase();
    const matches = q
        ? modules.flatMap((m) => Object.entries(m.actions)
            .filter(([k, d]) => `${m.name} ${d.label} ${d.desc} ${k}`.toLowerCase().includes(q))
            .map(([k, d]) => ({ mod: m, key: k, def: d })))
        : [];

    const actionRow = (mod, keyName, def, showModule = false) => {
        const on = isOn(mod.id, keyName);
        const reason = lockReason(mod.id, keyName);
        const locked = !on && Boolean(reason);
        const gated = keyName !== 'view' && !isOn(mod.id, 'view');
        const blockedButOn = on && ceiling && ceiling?.[mod.id]?.actions?.[keyName] === false;
        return (
            <div key={`${mod.id}.${keyName}`} className={`${styles.row} ${gated ? styles.rowGated : ''}`}>
                <div className={styles.rowText}>
                    <strong>
                        {showModule && <span className={styles.modTag}>{mod.name}</span>}
                        {def.label}
                        {RISK[def.risk] && <em className={def.risk === 'high' ? styles.riskHigh : styles.riskMed}>{RISK[def.risk]}</em>}
                        {blockedButOn && <em className={styles.blockedTag} title="On here, but the HQ limit for this branch turns it off"><Lock size={9} /> Blocked by HQ</em>}
                    </strong>
                    {def.desc && <small>{def.desc}</small>}
                    {gated && on && <small className={styles.gateNote}>Works only when "{mod.actions.view?.label || 'Open'}" is on.</small>}
                </div>
                {locked ? (
                    <span className={styles.lock} title={reason}><Lock size={11} /> {reason}</span>
                ) : (
                    <label className={styles.switch}>
                        <input type="checkbox" checked={on} disabled={readOnly} onChange={(e) => setAction(mod.id, keyName, e.target.checked)} aria-label={`${mod.name}: ${def.label}`} />
                        <span />
                    </label>
                )}
            </div>
        );
    };

    const mod = modules.find((m) => m.id === active) || modules[0];

    return (
        <div className={styles.wrap}>
            <aside className={styles.side}>
                <div className={styles.search}>
                    <Search size={13} />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search any button..." aria-label="Search permissions" />
                    {search && <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><X size={12} /></button>}
                </div>
                <div className={styles.total}><b>{totalOn}</b> of {total} on</div>
                {categories.map((cat) => {
                    const list = modules.filter((m) => m.category === cat.id);
                    if (!list.length) return null;
                    return (
                        <div key={cat.id} className={styles.cat}>
                            <span className={styles.catName}>{cat.name}</span>
                            {list.map((m) => {
                                const n = countOn(m);
                                const all = Object.keys(m.actions).length;
                                return (
                                    <button type="button" key={m.id} className={`${styles.modBtn} ${active === m.id && !q ? styles.modOn : ''}`} onClick={() => { setActive(m.id); setSearch(''); }}>
                                        <i className={isOn(m.id, 'view') ? styles.dotOn : styles.dotOff} />
                                        <span>{m.name}</span>
                                        <em>{n}/{all}</em>
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}
            </aside>

            <section className={styles.main}>
                {q ? (
                    <>
                        <header className={styles.head}><div><h3>Search results</h3><p>{matches.length} button(s) match "{search}"</p></div></header>
                        <div className={styles.list}>
                            {matches.map(({ mod: m, key, def }) => actionRow(m, key, def, true))}
                            {!matches.length && <p className={styles.empty}>Nothing found.</p>}
                        </div>
                    </>
                ) : mod && (
                    <>
                        <header className={styles.head}>
                            <div>
                                <h3>{mod.name}</h3>
                                <p>{mod.description}</p>
                            </div>
                            <div className={styles.bulk}>
                                {mod.actions.view && (
                                    <label className={`${styles.master} ${isOn(mod.id, 'view') ? styles.masterOn : ''}`} title="Off = the whole module is hidden from the menu and cannot be opened">
                                        <span>{isOn(mod.id, 'view') ? 'Module on (in menu)' : 'Module off (hidden)'}</span>
                                        <span className={styles.switch}>
                                            <input type="checkbox" checked={isOn(mod.id, 'view')} disabled={readOnly || (!isOn(mod.id, 'view') && !allowed(mod.id, 'view'))} onChange={(e) => setAction(mod.id, 'view', e.target.checked)} aria-label={`${mod.name} on or off`} />
                                            <span />
                                        </span>
                                    </label>
                                )}
                                {!readOnly && (
                                    <>
                                        <button type="button" onClick={() => setAll(mod.id, true)}>All on</button>
                                        <button type="button" onClick={() => setAll(mod.id, false)}>All off</button>
                                    </>
                                )}
                            </div>
                        </header>
                        <div className={styles.list}>
                            {Object.entries(mod.actions).map(([key, def]) => actionRow(mod, key, def))}
                        </div>

                        {Object.keys(mod.constraints || {}).length > 0 && (
                            <>
                                <h4 className={styles.subHead}>Rules</h4>
                                <div className={styles.list}>
                                    {Object.entries(mod.constraints).map(([key, def]) => {
                                        const v = value?.[mod.id]?.constraints?.[key];
                                        const lim = constraintLimit(mod.id, key);
                                        return (
                                            <div key={key} className={styles.row}>
                                                <div className={styles.rowText}>
                                                    <strong>{def.label}</strong>
                                                    {def.desc && <small>{def.desc}</small>}
                                                    {lim !== undefined && def.type !== 'toggle' && numberOr(lim) > 0 && <small className={styles.limitNote}>Max you can give: {def.type === 'select' ? (def.options.find((o) => String(o.value) === String(lim))?.label || lim) : `${lim} ${def.unit || ''}`}</small>}
                                                </div>
                                                {def.type === 'toggle' && (
                                                    lim === false && v !== true
                                                        ? <span className={styles.lock}><Lock size={11} /> {ceiling?.[mod.id]?.constraints?.[key] === false ? 'Blocked by HQ' : 'Above your access'}</span>
                                                        : (
                                                            <label className={styles.switch}>
                                                                <input type="checkbox" checked={v === true} disabled={readOnly} onChange={(e) => setConstraint(mod.id, key, def, e.target.checked)} aria-label={def.label} />
                                                                <span />
                                                            </label>
                                                        )
                                                )}
                                                {def.type === 'select' && (
                                                    <select className={styles.select} value={String(v ?? def.value)} disabled={readOnly} onChange={(e) => setConstraint(mod.id, key, def, e.target.value)} aria-label={def.label}>
                                                        {def.options.map((o) => (
                                                            <option key={o.value} value={o.value} disabled={lim !== undefined && numberOr(o.value) > numberOr(lim)}>{o.label}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                {def.type === 'number' && (
                                                    <div className={styles.numWrap}>
                                                        <input className={styles.num} type="number" min="0" value={v ?? def.value} disabled={readOnly} onChange={(e) => setConstraint(mod.id, key, def, e.target.value)} aria-label={def.label} />
                                                        <span>{def.unit || ''}{numberOr(v) === 0 ? ' (no limit)' : ''}</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}
            </section>
        </div>
    );
}